import { Link } from "react-router-dom";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";
import { BookingData, isUpcomingBooking } from "../utils/bookingUtils";
import {
  BookingStatus,
  BookingType,
} from "../constants-generated/well-known-values";

interface HostWorkspaceCardProps {
  workspace: WorkspaceCardData;
  bookings: BookingData[];
  onManage?: () => void;
}

export default function HostWorkspaceCard({
  workspace,
  bookings,
  onManage,
}: HostWorkspaceCardProps) {
  const reservations = bookings.filter(
    (b) => b.bookingType === BookingType.Reservation
  );
  const tours = bookings.filter((b) => b.bookingType === BookingType.Tour);
  const earned = bookings
    .filter((b) => b.status === BookingStatus.Completed)
    .reduce((s, b) => s + b.totalPrice, 0);
  const projected = bookings
    .filter(
      (b) =>
        b.status === BookingStatus.Confirmed && isUpcomingBooking(b.endDate)
    )
    .reduce((s, b) => s + b.totalPrice, 0);

  const imageUrl = workspace.images?.[0]?.imageUrl;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      {/* Image */}
      <div className="relative h-48 bg-gray-100 flex-shrink-0">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={workspace.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <svg
              className="w-12 h-12 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
              />
            </svg>
          </div>
        )}
        {/* Type + Furnished badges */}
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {workspace.workspaceType && (
            <span className="bg-[var(--secondary)] text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
              {workspace.workspaceType}
            </span>
          )}
          {workspace.isFurnished && (
            <span className="bg-white/90 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
              Furnished
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-900 text-base mb-0.5 truncate">
          {workspace.title}
        </h3>
        <p className="text-sm text-gray-500 truncate mb-1">
          {workspace.address}, {workspace.city}
        </p>
        <p className="text-sm font-semibold text-gray-800 mb-4">
          ${workspace.pricePerYear.toLocaleString()}
          <span className="font-normal text-gray-500">/yr</span>
        </p>

        {/* Stats bar */}
        {bookings.length > 0 ? (
          <div className="flex-1">
            <div className="flex gap-2 flex-wrap mb-3">
              <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                {reservations.length} Booking
                {reservations.length !== 1 ? "s" : ""}
              </span>
              <span className="bg-purple-50 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full">
                {tours.length} Tour{tours.length !== 1 ? "s" : ""}
              </span>
              <span className="bg-green-50 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full">
                ${earned.toLocaleString()} Earned
              </span>
            </div>

            {projected > 0 && (
              <p className="text-xs text-gray-500 mb-4">
                <span className="font-semibold text-gray-700">
                  ${projected.toLocaleString()}
                </span>{" "}
                projected from upcoming confirmed bookings
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic flex-1 mb-4">
            No activity yet
          </p>
        )}

        {/* Footer buttons */}
        <div className="flex gap-2 pt-3 border-t border-gray-100 mt-auto">
          <Link
            to={`/workspace/${workspace.workspaceId}`}
            className="flex-1 text-center text-sm font-medium text-gray-700 border border-gray-200 rounded-lg py-2 hover:bg-gray-50 transition-colors"
          >
            View Listing
          </Link>
          <button
            onClick={onManage}
            className="flex-1 text-sm font-medium text-white bg-[var(--secondary)] rounded-lg py-2 hover:opacity-90 transition-opacity"
          >
            Manage
          </button>
        </div>
      </div>
    </div>
  );
}
